import React from 'react';

type DebugCallbackProps = {
  /** Number of ticks currently tracked */
  numTicks: number;
};
type DebugCallback = (props: DebugCallbackProps) => void;

type Props = {
  /** Number of ticks required to trigger the callback */
  numTicks: number;
  /** Callback to trigger */
  onTickCountReached: () => void;
  /**
   * Time in milliseconds to wait for the next tick before resetting the counter
   * @default 2000
   */
  tickCountResetMs?: number;
  /**
   * Whether the hook is enabled
   * @default true
   */
  enabled?: boolean;
  /** Optional debug options */
  debug?: Partial<{
    /** Called every time the effect runs. Not called if the hook is disabled */
    onEffectCalled: DebugCallback;
    /** Called when the tick count is reset */
    onResetTickCount: DebugCallback;
  }>;
};

/** Fires the given callback when a given number of ticks is reached */
export const useTickerAction = ({
  numTicks,
  onTickCountReached,
  tickCountResetMs = 2000,
  enabled = true,
  debug = {},
}: Props) => {
  const [tickCount, setTickCount] = React.useState(0);
  const tapTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const resetTickCount = () => {
    debug.onResetTickCount?.({numTicks: tickCount});
    setTickCount(0);
  };
  // handle logo screen taps side-effects
  React.useEffect(() => {
    if (!enabled) return;
    const debugProps = {numTicks: tickCount};
    debug.onEffectCalled?.(debugProps);
    if (tapTimeout.current) clearTimeout(tapTimeout.current);

    if (tickCount >= numTicks) {
      onTickCountReached();
      setTickCount(0);
    } else if (tickCount > 0) {
      tapTimeout.current = setTimeout(resetTickCount, tickCountResetMs);
    }

    // Don't do anything if tickCount is 0
  }, [tickCount]);

  return {
    incrementTickCount: () => setTickCount(prev => prev + 1),
    decrementTickCount: () => setTickCount(prev => prev - 1),
    resetTickCount,
  };
};
